import { useState, useEffect } from "react";
import { ArrowUpDownIcon, XIcon } from "lucide-react";
import { Button } from "@component/button";
import { Badge } from "@component/badge";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@component/popover";
import { JobSortState } from "@type/job";
import { cn } from "@util/tailwind";

interface SortPanelProps {
    optionName: string;
    sortOptions: JobSortState;
    onSortChange: (sortOptions: JobSortState) => void;
    className?: string;
    disabledAlpha?: boolean;
}

export default function SortPanelDropdown({
    optionName,
    sortOptions,
    onSortChange,
    className = "",
    disabledAlpha = false,
}: SortPanelProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [tempSortOptions, setTempSortOptions] = useState(sortOptions);

    useEffect(() => {
        const savedSortOptions = localStorage.getItem(optionName);
        if (savedSortOptions) {
            try {
                const parsed = JSON.parse(savedSortOptions);
                setTempSortOptions(parsed);
            } catch (error) {
                console.error("Error loading saved sort options:", error);
            }
        }
    }, []);

    useEffect(() => {
        setTempSortOptions(sortOptions);
    }, [sortOptions]);

    const handleDateCreatedChange = (value: string) => {
        setTempSortOptions((prev) => ({
            ...prev,
            job_date: prev.job_date === value ? "" : value,
        }));
    };

    const handleAlphabeticalChange = (value: string) => {
        setTempSortOptions((prev) => ({
            ...prev,
            job_title: prev.job_title === value ? "" : value,
        }));
    };

    const handleClearAll = () => {
        setTempSortOptions({ job_date: "", job_title: "" });
    };

    const handleApply = () => {
        localStorage.setItem(optionName, JSON.stringify(tempSortOptions));
        onSortChange(tempSortOptions);
        setIsOpen(false);
    };

    const handleCancel = () => {
        setTempSortOptions(sortOptions);
        setIsOpen(false);
    };

    const activeSortCount = (sortOptions.job_date ? 1 : 0) + (sortOptions.job_title ? 1 : 0);
    const tempActiveSortCount = (tempSortOptions.job_date ? 1 : 0) + (tempSortOptions.job_title ? 1 : 0);

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    className={cn(
                        "h-9",
                        className,
                        isOpen && "border-blue-600 bg-blue-50 dark:bg-blue-950 dark:border-blue-600"
                    )}
                >
                    <ArrowUpDownIcon className="h-4 w-4 mr-2" />
                    Sort
                    {activeSortCount > 0 && (
                        <Badge
                            variant="secondary"
                            className="ml-2 bg-blue-600 text-white hover:bg-blue-600 h-5 min-w-[10px] flex items-center justify-center px-2.5 rounded-md"
                        >
                            {activeSortCount}
                        </Badge>
                    )}
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[280px] p-0" align="end">
                <div className="p-4 border-b flex items-center justify-between">
                    <h4 className="font-semibold text-sm">Sort Jobs by</h4>
                    {tempActiveSortCount > 0 && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleClearAll}
                            className="h-auto p-0 text-xs text-blue-600 hover:text-blue-700 hover:bg-transparent"
                        >
                            Clear all ({tempActiveSortCount})
                        </Button>
                    )}
                </div>
                <div className="p-4 space-y-6">

                    {/* Date Created Section */}
                    <div className="space-y-3">
                        <h4 className="font-semibold text-sm">Date Created</h4>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDateCreatedChange("desc")}
                                className={cn(
                                    "h-8 rounded-full transition-colors",
                                    tempSortOptions.job_date === "desc"
                                        ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700 hover:text-white"
                                        : "hover:border-blue-300"
                                )}
                            >
                                Newest First
                                {tempSortOptions.job_date === "desc" && (
                                    <XIcon className="ml-1.5 h-3 w-3" />
                                )}
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDateCreatedChange("asc")}
                                className={cn(
                                    "h-8 rounded-full transition-colors",
                                    tempSortOptions.job_date === "asc"
                                        ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700 hover:text-white"
                                        : "hover:border-blue-300"
                                )}
                            >
                                Oldest First
                                {tempSortOptions.job_date === "asc" && (
                                    <XIcon className="ml-1.5 h-3 w-3" />
                                )}
                            </Button>
                        </div>
                    </div>

                    {/* Alphabetical Order Section */}
                    {!disabledAlpha && <div className="space-y-3">
                        <h4 className="font-semibold text-sm">Alphabetical Order</h4>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleAlphabeticalChange("asc")}
                                className={cn(
                                    "h-8 rounded-full transition-colors",
                                    tempSortOptions.job_title === "asc"
                                        ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700 hover:text-white"
                                        : "hover:border-blue-300"
                                )}
                            >
                                A to Z
                                {tempSortOptions.job_title === "asc" && (
                                    <XIcon className="ml-1.5 h-3 w-3" />
                                )}
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleAlphabeticalChange("desc")}
                                className={cn(
                                    "h-8 rounded-full transition-colors",
                                    tempSortOptions.job_title === "desc"
                                        ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700 hover:text-white"
                                        : "hover:border-blue-300"
                                )}
                            >
                                Z to A
                                {tempSortOptions.job_title === "desc" && (
                                    <XIcon className="ml-1.5 h-3 w-3" />
                                )}
                            </Button>
                        </div>
                    </div>}

                </div>

                <div className="p-4 border-t flex gap-2">
                    <Button
                        variant="outline"
                        onClick={handleCancel}
                        className="flex-1"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleApply}
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                    >
                        Apply
                    </Button>
                </div>
            </PopoverContent>
        </Popover>
    );
}
